import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle } from "lucide-react";

type CallbackState = "loading" | "success" | "error";

export default function AuthCallback() {
  const [, setLocation] = useLocation();
  const [state, setState] = useState<CallbackState>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    async function handleCallback() {
      try {
        // Get the hash fragment from the URL
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        const type = hashParams.get("type");
        const errorParam = hashParams.get("error");
        const errorDescription = hashParams.get("error_description");

        // Check for errors in URL
        if (errorParam) {
          setErrorMessage(errorDescription || errorParam);
          setState("error");
          return;
        }

        // If we have tokens, set the session
        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            setErrorMessage(error.message);
            setState("error");
            return;
          }

          // Email verification successful
          if (type === "signup" || type === "email") {
            setState("success");
            // Redirect to dashboard after a short delay
            setTimeout(() => {
              setLocation("/dashboard");
            }, 2000);
            return;
          }

          // For other types (like password recovery), redirect appropriately
          if (type === "recovery") {
            setLocation("/reset-password" + window.location.hash);
            return;
          }

          // Default: redirect to dashboard
          setState("success");
          setTimeout(() => {
            setLocation("/dashboard");
          }, 2000);
        } else {
          // No tokens found - try to get session from URL automatically
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            setErrorMessage(error.message);
            setState("error");
            return;
          }

          if (data.session) {
            setState("success");
            setTimeout(() => {
              setLocation("/dashboard");
            }, 2000);
          } else {
            setErrorMessage("No authentication data found. Please try signing up again.");
            setState("error");
          }
        }
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : "An unexpected error occurred");
        setState("error");
      }
    }

    handleCallback();
  }, [setLocation]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link href="/" className="flex flex-col items-center gap-2">
            <img src="/logo.png" alt="ReviewKit" className="h-12 w-auto object-contain" />
            <span className="font-bold text-xl text-muted-foreground">ReviewKit</span>
          </Link>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {state === "loading" && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mx-auto">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">Verifying your email...</h2>
                <p className="text-muted-foreground">Please wait while we confirm your account.</p>
              </div>
            </>
          )}

          {state === "success" && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mx-auto">
                <CheckCircle className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">Email verified!</h2>
                <p className="text-muted-foreground">
                  Your account is now active. Redirecting you to your dashboard...
                </p>
              </div>
            </>
          )}

          {state === "error" && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mx-auto">
                <XCircle className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">Verification failed</h2>
                <p className="text-muted-foreground">{errorMessage}</p>
              </div>
            </>
          )}
        </CardContent>
        {state === "error" && (
          <CardFooter className="flex flex-col gap-3">
            <Link href="/signup" className="w-full">
              <Button className="w-full">Try signing up again</Button>
            </Link>
            <p className="text-sm text-muted-foreground text-center">
              Already have an account?{" "}
              <Link href="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}




